import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Save,
  Upload,
  Trash2,
  Image as ImageIcon,
  Loader2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Hash,
  FileText,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useToast } from '../../../components/ui/toaster.jsx';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import {
  fetchTenantSettings,
  updateTenantSettings,
  uploadLogoToR2,
  deleteLogo,
  LOGO_LIMITS,
} from './Services/tenantSettingsService';
import { COMPANY_FIELDS, TEMPLATE_FIELDS } from './utils/tenantSettingsColumns';
import { cn } from '../../../lib/utils';

const LOGO_MAX_BYTES = LOGO_LIMITS.maxSizeMB * 1024 * 1024;

export default function GeneralSettingsPage() {
  const toast = useToast();
  const { tenant, isPlatformMode } = useAuth();
  const [data, setData] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);
  const logoInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchTenantSettings();
      if (!r?.success) {
        toast.error('Error al cargar la configuración', r?.message);
        return;
      }
      setData(r.data);
      setFormData(r.data || {});
    } catch (e) {
      toast.error('Error al cargar la configuración', e?.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setFormData((p) => ({ ...p, [key]: value }));
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    return COMPANY_FIELDS.concat(TEMPLATE_FIELDS).some((f) => {
      const a = (formData[f.id] ?? '').toString();
      const b = (data[f.id] ?? '').toString();
      return a !== b;
    });
  }, [formData, data]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = COMPANY_FIELDS.concat(TEMPLATE_FIELDS).reduce(
        (acc, f) => {
          const v = (formData[f.id] ?? '').toString().trim();
          if (v) acc[f.id] = v;
          return acc;
        },
        {},
      );
      const r = await updateTenantSettings(payload);
      if (!r?.success) {
        toast.error('No se pudo guardar', r?.message);
        return;
      }
      toast.success('Configuración guardada');
      load();
    } catch (e) {
      toast.error('Error al guardar', e?.message);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Logo ----------

  const triggerLogoUpload = () => {
    logoInputRef.current?.click();
  };

  const onLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > LOGO_MAX_BYTES) {
      toast.error(
        'Logo demasiado grande',
        `Máximo permitido: ${LOGO_LIMITS.maxSizeMB} MB`,
      );
      return;
    }
    if (!file.type) {
      toast.error('Tipo de archivo no detectado');
      return;
    }

    setLogoUploading(true);
    setLogoProgress(0);
    try {
      const r = await uploadLogoToR2(file, { onProgress: setLogoProgress });
      if (!r?.success) {
        toast.error('No se pudo subir el logo', r?.message);
        return;
      }
      toast.success('Logo actualizado', file.name);
      load();
    } catch (e) {
      toast.error('Error al subir el logo', e?.message || 'Intenta nuevamente');
    } finally {
      setLogoUploading(false);
      setLogoProgress(0);
    }
  };

  const onDeleteLogo = async () => {
    if (!window.confirm('¿Eliminar el logo actual?')) return;
    try {
      const r = await deleteLogo();
      if (!r?.success) {
        toast.error('No se pudo eliminar', r?.message);
        return;
      }
      toast.success('Logo eliminado');
      load();
    } catch (e) {
      toast.error('Error', e?.message);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Cargando configuración…
      </div>
    );
  }

  if (isPlatformMode) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="font-medium text-foreground">Configuración por operador</p>
          <p className="text-sm mt-1">
            Esta sección sólo está disponible dentro de un operador (subdominio). Entrá
            como operador para configurar su logo, datos y plantillas.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <Card>
        <CardContent className="p-4 md:p-5">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-3 rounded-lg shrink-0">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground">
                Configuración General
              </h1>
              <p className="text-sm text-muted-foreground">
                Logo, datos de la empresa y plantillas de mensaje del operador
                {tenant?.nombre || tenant?.slug ? ` ${tenant.nombre || tenant.slug}` : ''}.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo + datos de la empresa */}
      <Card>
        <CardHeader className="pb-4 border-b border-border">
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4 text-primary" />
            Identidad de la empresa
          </CardTitle>
          <CardDescription>
            El logo se guarda en R2. PNG con fondo transparente funciona mejor.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-5 space-y-6">
          <LogoSection
            data={data}
            uploading={logoUploading}
            progress={logoProgress}
            onUpload={triggerLogoUpload}
            onDelete={onDeleteLogo}
          />

          <div className="border-t border-border pt-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {COMPANY_FIELDS.map((f) => (
                <Field key={f.id} {...f}>
                  <Input
                    value={formData[f.id] ?? ''}
                    onChange={(e) => setField(f.id, e.target.value)}
                    maxLength={f.maxLength}
                    placeholder={f.placeholder}
                    autoComplete="off"
                    icon={ICON_BY_FIELD[f.id]}
                  />
                </Field>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plantillas de mensajes */}
      <Card>
        <CardHeader className="pb-4 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4 text-primary" />
                Plantillas de mensaje
                <Badge variant="outline" className="font-normal text-[10px] py-0 px-1.5">
                  Para uso futuro
                </Badge>
              </CardTitle>
              <CardDescription>
                Estos textos se usarán en encabezados y pies de documentos (embarque,
                factura, correos). Ya podés cargarlos — se aplican cuando se liberen los
                features que los consumen.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-5 space-y-4">
          {TEMPLATE_FIELDS.map((f) => (
            <div key={f.id} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {f.label}
                </Label>
              </div>
              <textarea
                rows={f.rows || 3}
                value={formData[f.id] ?? ''}
                onChange={(e) => setField(f.id, e.target.value)}
                placeholder={f.placeholder}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              {f.helper && (
                <p className="text-xs text-muted-foreground">{f.helper}</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-10 -mx-4 px-4 py-3 bg-background/80 backdrop-blur border-t border-border">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {dirty
              ? 'Tenés cambios sin guardar'
              : data
                ? 'Todo al día'
                : 'Cargá los datos y guardá'}
          </p>
          <Button onClick={handleSave} disabled={!dirty || saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Guardando…' : 'Guardar configuración'}
          </Button>
        </div>
      </div>

      <input
        ref={logoInputRef}
        type="file"
        accept={LOGO_LIMITS.accept}
        onChange={onLogoSelected}
        className="hidden"
        aria-hidden="true"
      />
    </motion.div>
  );
}

// ---------- Subcomponentes ----------

const ICON_BY_FIELD = {
  nombreComercial: Building2,
  razonSocial: Building2,
  nit: Hash,
  direccion: MapPin,
  telefono: Phone,
  correo: Mail,
  sitioWeb: Globe,
};

function Field({ id, label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={id}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <div className="relative">
        {children}
      </div>
    </div>
  );
}

function LogoSection({ data, uploading, progress, onUpload, onDelete }) {
  const logo = data?.logo;
  return (
    <div className="flex flex-col md:flex-row items-start gap-6">
      <div className="shrink-0">
        <div
          className={cn(
            'h-28 w-28 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden bg-muted/30',
            logo ? 'border-solid border-border' : 'border-input',
          )}
        >
          {logo ? (
            <img
              src={logo.url}
              alt="Logo"
              className="h-full w-full object-contain"
            />
          ) : (
            <ImageIcon className="h-10 w-10 text-muted-foreground/50" />
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Logo del operador</h3>
          <p className="text-xs text-muted-foreground">
            {logo
              ? `Subido: ${new Date(logo.uploadedAt).toLocaleString('es-ES')} · ${(logo.size / 1024).toFixed(1)} KB`
              : `Sin logo todavía. Formatos: PNG, JPG, WebP, SVG. Máx ${LOGO_LIMITS.maxSizeMB} MB.`}
          </p>
        </div>

        {uploading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <div className="flex-1 h-1.5 rounded bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="shrink-0 tabular-nums">{progress}%</span>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            onClick={onUpload}
            disabled={uploading}
            size="sm"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {logo ? 'Reemplazar logo' : 'Subir logo'}
          </Button>
          {logo && !uploading && (
            <Button type="button" variant="outline" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
              Eliminar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
